import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import UniversalLayout from "./components/UniversalLayout";
import MdxRenderer from "./components/blog/MdxRenderer";
import { BLOG_ROOT_PATH, getBlogPostBySlug } from "./content/blogPosts";
import { useTheme } from "./hooks/useTheme";
import "./BlogPostPage.css";

function formatPublishedDate(dateString) {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function getCoverImageSource(post, isDarkTheme) {
  if (isDarkTheme && post.coverImageDarkPath) {
    return post.coverImageDarkPath;
  }
  return post.coverImagePath || "/latted_social_preview_dark.png";
}

function useReadingProgress(ref) {
  const [progress, setProgress] = useState(0);

  const handleScroll = useCallback(() => {
    if (!ref.current) return;
    const el = ref.current;
    const rect = el.getBoundingClientRect();
    const total = el.scrollHeight;
    const scrolled = Math.max(0, -rect.top);
    const visible = window.innerHeight;
    const pct = Math.min(100, Math.max(0, (scrolled / (total - visible)) * 100));
    setProgress(pct);
  }, [ref]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return progress;
}

function BlogPostPage() {
  const { slug } = useParams();
  const post = useMemo(() => getBlogPostBySlug(slug || ""), [slug]);
  const isDarkTheme = useTheme();
  const [source, setSource] = useState("");
  const [loadError, setLoadError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const articleRef = useRef(null);
  const readingProgress = useReadingProgress(articleRef);

  useEffect(() => {
    if (!post) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setLoadError("");

    async function loadMdxSource() {
      try {
        const response = await fetch(post.mdxPublicPath, {
          headers: {
            Accept: "text/plain",
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load article source: ${response.status}`);
        }

        const mdxText = await response.text();
        if (!cancelled) {
          setSource(mdxText);
          setIsLoading(false);
        }
      } catch (error) {
        console.error("[BlogPostPage] Failed to load post:", error);
        if (!cancelled) {
          setLoadError(
            "This article is temporarily unavailable. Please try again in a moment."
          );
          setIsLoading(false);
        }
      }
    }

    loadMdxSource();
    return () => {
      cancelled = true;
    };
  }, [post]);

  if (!post) {
    return <Navigate to={BLOG_ROOT_PATH} replace />;
  }

  const coverImageSource = getCoverImageSource(post, isDarkTheme);

  return (
    <UniversalLayout>
      <div
        className="blog-post-page__progress-bar"
        role="progressbar"
        aria-valuenow={Math.round(readingProgress)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Reading progress"
        style={{ "--reading-progress": `${readingProgress}%` }}
      />

      <div className="blog-post-page" ref={articleRef}>
        <nav className="blog-post-page__breadcrumb" aria-label="Breadcrumb">
          <ol>
            <li><Link to="/">Home</Link></li>
            <li><Link to={BLOG_ROOT_PATH}>Blog</Link></li>
            <li aria-current="page">{post.title}</li>
          </ol>
        </nav>

        <article className="blog-post-page__article" itemScope itemType="https://schema.org/BlogPosting">
          <header className="blog-post-page__header">
            <div className="blog-post-page__meta-row">
              <time dateTime={post.publishedAt} itemProp="datePublished">
                {formatPublishedDate(post.publishedAt)}
              </time>
              <span aria-hidden="true">·</span>
              <span>{post.readingTimeMinutes} min read</span>
              <span aria-hidden="true">·</span>
              <span itemProp="author" itemScope itemType="https://schema.org/Person">
                <span itemProp="name">{post.authorName}</span>
              </span>
            </div>

            <h1 className="blog-post-page__title" itemProp="headline">{post.title}</h1>
            <p className="blog-post-page__description" itemProp="description">{post.description}</p>

            <div className="blog-post-page__tags" aria-label="Post tags">
              {post.tags.map((tag) => (
                <span key={tag} className="blog-post-page__tag" itemProp="keywords">
                  {tag}
                </span>
              ))}
            </div>
          </header>

          <img
            src={coverImageSource}
            alt={post.coverImageAlt || post.title}
            className="blog-post-page__cover"
            loading="eager"
            itemProp="image"
            onError={(event) => {
              event.currentTarget.src = "/latted_social_preview_dark.png";
            }}
          />

          {isLoading ? (
            <div className="blog-post-page__status">
              <div className="blog-post-page__skeleton" />
              <div className="blog-post-page__skeleton blog-post-page__skeleton--short" />
              <div className="blog-post-page__skeleton" />
            </div>
          ) : loadError ? (
            <p className="blog-post-page__status blog-post-page__status--error">
              {loadError}
            </p>
          ) : (
            <div itemProp="articleBody">
              <MdxRenderer source={source} className="blog-post-page__content" />
            </div>
          )}
        </article>
      </div>
    </UniversalLayout>
  );
}

export default BlogPostPage;

